"use client"

import { withAmazonTag } from "@/lib/affiliate"
import { ExternalLink, Package, ArrowRight } from "lucide-react"
import { contentAnalytics, extractAsin } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import type { SidebarProduct } from "@/components/guides/guide-sidebar-product"

/** Surface and placement_key for every event this row emits. */
export const BLOG_RAIL_MOBILE_SURFACE = "blog-rail-mobile"

interface BlogRailMobilePicksProps {
  slug: string
  products: SidebarProduct[]
  productSource: "curated" | "category-fallback"
  /** Viewport class that hides the row wherever the rail shows. */
  className?: string
}

/**
 * The rail's product card style, laid out as one in-flow row after the article
 * body for viewports where the rail is hidden. Same visual language as
 * GuideSidebarProduct; its own surface so its CTR reads separately from the
 * rail and the footer block it replaces below the rail breakpoint.
 */
export function BlogRailMobilePicks({ slug, products, productSource, className = "" }: BlogRailMobilePicksProps) {
  const containerRef = useAffiliateImpression(BLOG_RAIL_MOBILE_SURFACE, {
    article_slug: slug,
    placement: BLOG_RAIL_MOBILE_SURFACE,
    product_count: products.length,
    product_source: productSource,
    denominator: "container",
  })

  if (products.length === 0) return null

  return (
    <div className={`mt-8 mb-8 ${className}`}>
      <div
        ref={containerRef}
        className="ds-glass rounded-[18px] p-4"
        style={{ boxShadow: "0 4px 16px -8px rgba(18,46,30,0.22), 0 1px 3px -1px rgba(18,46,30,0.12)" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="grid place-items-center w-6 h-6 rounded-lg border border-white/70 bg-green-50/85 text-green-700 flex-shrink-0">
            <Package className="w-3.5 h-3.5" />
          </span>
          <h2 className="text-[11px] font-bold text-green-700 uppercase tracking-[0.08em]">
            Top Picks
          </h2>
        </div>

        <ul className="m-0 list-none p-0 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {products.map((product, index) => (
            <PickItem
              key={`${product.affiliateUrl}-${index}`}
              slug={slug}
              product={product}
              position={index + 1}
              productSource={productSource}
            />
          ))}
        </ul>

        <p className="mt-3 text-xs text-gray-500">
          As an Amazon Associate we earn from qualifying purchases.
        </p>
      </div>
    </div>
  )
}

function PickItem({
  slug,
  product,
  position,
  productSource,
}: {
  slug: string
  product: SidebarProduct
  position: number
  productSource: "curated" | "category-fallback"
}) {
  const asin = extractAsin(product.affiliateUrl)
  const href = withAmazonTag(product.affiliateUrl, "rail")
  const itemRef = useAffiliateImpression(BLOG_RAIL_MOBILE_SURFACE, {
    article_slug: slug,
    placement: BLOG_RAIL_MOBILE_SURFACE,
    denominator: "product",
    position,
    product_name: product.name,
    affiliate_link: product.affiliateUrl,
    product_source: productSource,
    ...(asin ? { asin } : {}),
  })

  const handleClick = () => {
    contentAnalytics.affiliateLinkClicked(slug, product.name, product.affiliateUrl, "blog-rail-mobile", asin)
    contentAnalytics.affiliateProductClicked(BLOG_RAIL_MOBILE_SURFACE, {
      productName: product.name,
      affiliateLink: product.affiliateUrl,
      asin,
      article_slug: slug,
      placement: BLOG_RAIL_MOBILE_SURFACE,
      position,
      product_source: productSource,
    })
  }

  return (
    <li ref={itemRef} className="list-none">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow sponsored"
        data-affiliate="true"
        data-affiliate-placement={BLOG_RAIL_MOBILE_SURFACE}
        onClick={handleClick}
        className="group flex h-full items-start gap-3 rounded-2xl border border-white/55 bg-white/55 backdrop-blur-md p-2.5 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_-14px_rgba(22,163,74,0.4)]"
      >
        <span className="grid h-12 w-12 flex-shrink-0 place-items-center overflow-hidden rounded-lg border border-white/70 bg-white">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt="" loading="lazy" className="h-full w-full object-contain p-1" />
          ) : (
            <Package className="w-5 h-5 text-green-600/70" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5">
            <span className="text-[13px] font-semibold text-gray-900 group-hover:text-green-700 leading-snug flex-1">
              {product.name}
            </span>
            <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-green-600" />
          </div>
          <span className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-semibold text-green-700 group-hover:text-green-800">
            Check price on Amazon
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </a>
    </li>
  )
}
